import type { Permission } from "@sitescene/constant";

import { UserStatus } from "@sitescene/types";
import { AlertCircle, Lock01 } from "@untitledui/icons";
import { type PropsWithChildren } from "react";
import { Navigate, Outlet, useLocation } from "react-router";

import { SensorsOfflineBanner } from "@/components/banners/sensors-offline-banner";
import { LoadingIndicator } from "@/components/loading-indicator/loading-indicator";
import { StatusMessage } from "@/components/status-messages/status-message";
import { Button } from "@/components/ui/buttons/button";
import { ROUTES } from "@/constants/routes";
import { captureRedirectDestinationForLogin } from "@/features/auth/auth-session-storage";
import { ReportProblemButton } from "@/features/feedback/components/report-problem-button";
import { useMe } from "@/features/users/hooks/useMe";
import { usePermissions } from "@/hooks/usePermissions";
import { MainLayout } from "@/layouts/MainLayout";
import { useAuth } from "@/providers/auth-provider";
import { hasFirstAndLastName } from "@/utils/profile-guards";

/**
 * Shared loading component for authentication checks
 */
interface AuthLoadingStateProps {
	loadingLabel?: string;
}
const AuthLoadingState = ({
	loadingLabel = "Checking authentication",
}: AuthLoadingStateProps) => (
	<div className="flex min-h-screen items-center justify-center">
		<LoadingIndicator label={loadingLabel} size="md" />
	</div>
);

/**
 * Shared authentication check logic
 * Returns the redirect component if not authenticated, null if authenticated
 */
const useAuthCheck = () => {
	const { isAuthenticated, isLoadingAuth } = useAuth();
	const location = useLocation();
	if (isLoadingAuth) {
		return { isLoading: true };
	}

	if (!isAuthenticated) {
		// If the user is not authenticated, capture the destination path and store it in sessionStorage so that it can be used after login.
		captureRedirectDestinationForLogin(
			location.pathname,
			location.search,
			location.hash,
		);
		return {
			redirect: (
				<Navigate replace state={{ from: location }} to={ROUTES.LOGIN} />
			),
		};
	}

	return { isAuthenticated: true };
};

/**
 * AuthGuard component
 *
 * Protects routes by checking if the user is authenticated.
 * If not authenticated, redirects to the login page.
 * If authenticated, renders the children (protected content).
 */
export const AuthGuard = ({ children }: PropsWithChildren) => {
	const authCheck = useAuthCheck();

	if (authCheck.isLoading) {
		return <AuthLoadingState />;
	}

	if (authCheck.redirect) {
		return authCheck.redirect;
	}

	return <>{children}</>;
};

/**
 * ProtectedLayout component
 *
 * Layout route wrapper that protects all nested routes.
 * Use this as a parent route to protect multiple routes at once.
 *
 * **Profile fetch vs `LoadingOverlay`:** The global `LoadingOverlay` only draws the spinner or error UI on
 * top of the app; it does not stop React from mounting children. This layout must avoid rendering
 * `<Outlet />` until `useUser` has finished loading or surfaced a terminal error without `userData`.
 * Otherwise nested routes would mount without `userData`, which risks effects running on incomplete state,
 * wrong activation redirects, and failures visible under or beside the overlay.
 */
export const ProtectedLayout = () => {
	const authCheck = useAuthCheck();
	const { authUser } = useAuth();
	const location = useLocation();
	const { isLoadingUser, isUserError, userData } = useMe();

	if (authCheck.isLoading) {
		return <AuthLoadingState />;
	}

	if (authCheck.redirect) {
		return authCheck.redirect;
	}

	// Defer rendering the protected outlet until the user profile is loaded or an error is surfaced.
	const deferProtectedOutlet =
		Boolean(authUser?.id) &&
		((isLoadingUser && !isUserError) || (isUserError && !userData));

	if (deferProtectedOutlet) {
		return null;
	}

	if (userData) {
		const accountActivated =
			hasFirstAndLastName(userData) && userData.status !== UserStatus.Invited;
		const onAccountActivationPage =
			location.pathname === ROUTES.ACCOUNT_ACTIVATION;
		// If the user is not on the account activation page and has not completed their profile, redirect to the account activation page.
		if (!accountActivated && !onAccountActivationPage) {
			return <Navigate replace to={ROUTES.ACCOUNT_ACTIVATION} />;
		}
		// If the user tries to access the account activation page and has already completed their profile, redirect to the dashboard.
		if (accountActivated && onAccountActivationPage) {
			return <Navigate replace to={ROUTES.DASHBOARD} />;
		}
	}

	return (
		<>
			<SensorsOfflineBanner />
			{/* No max-h-dvh / overflow-hidden here on purpose: the dashboard targets
			    short viewports (laptop, mobile) where document-scroll is preferred over
			    container-clipping. Pages render at their intrinsic height. */}
			<div style={{ paddingTop: "var(--sensor-banner-height, 0px)" }}>
				<Outlet />
			</div>
			<ReportProblemButton />
		</>
	);
};

interface PermissionGuardProps {
	/**
	 * The permission required to access the nested routes.
	 */
	permission: Permission;
}

/**
 * Layout route that protects nested routes behind a single permission.
 *
 * Place this as a parent `<Route element={...}>` in the router to gate
 * any number of child routes with one permission check. Uses the same
 * three-state logic as `checkPermission`:
 *
 * - Error         → renders an inline error state with a retry button
 * - Denied        → renders an inline "Access Denied" state within the app shell
 * - Still loading → spinner (prevents a flash of content or premature redirect)
 * - Allowed       → renders `<Outlet />`
 */
export const PermissionGuard = ({ permission }: PermissionGuardProps) => {
	const { checkPermission, error, refetch } = usePermissions();
	const { allowed, denied } = checkPermission(permission);

	if (error) {
		return (
			<MainLayout
				errorComponent={
					<StatusMessage
						description="We couldn't verify your permissions. Please try again, or contact support if the problem persists."
						icon={AlertCircle}
						includeCallToAction
						refetch={() => {
							void refetch();
						}}
						title="Unable to load permissions"
						type="error"
					/>
				}
				isError
				title="Unable to load permissions"
			>
				{null}
			</MainLayout>
		);
	}

	if (denied) {
		return (
			<MainLayout
				errorComponent={
					<StatusMessage
						description={
							<>
								You don't have permission to view this page.{" "}
								<Button color="link-color" href={ROUTES.SUPPORT} size="lg">
									Contact Support
								</Button>{" "}
								if you believe this is a mistake or request to enable this
								feature for your account.
							</>
						}
						icon={Lock01}
						navigate={true}
						title="Access Denied"
						type="warning"
					/>
				}
				isError
				title="Access Denied"
			>
				{null}
			</MainLayout>
		);
	}

	// Still loading (or auth not yet resolved) — hold rendering.
	if (!allowed) return <AuthLoadingState loadingLabel="Checking permissions" />;

	return <Outlet />;
};

export { FeatureFlagGuard } from "./feature-flag-guard";
